import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Check, X, Loader2 } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { restoTrasSenal } from "@/lib/reserva";
import { Button } from "@/components/ui/button";
import { eur } from "@/lib/format";

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 8;

export default function PaymentResult({ kind = "deposit" }) {
  const { result } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const code = params.get("code");
  const [status, setStatus] = useState("checking");
  const [holdMode, setHoldMode] = useState(false);
  const [signalInfo, setSignalInfo] = useState(null);
  const [extrasInfo, setExtrasInfo] = useState(null); // {amount, paid}
  const [error, setError] = useState("");

  useEffect(() => {
    if (result === "cancelado") {
      setStatus("cancelled");
      return;
    }
    if (!sessionId) {
      setStatus("error");
      setError("Sesión de pago no encontrada");
      return;
    }
    let attempts = 0;
    const endpoint = kind === "extras"
      ? `/payments/extras/status/${sessionId}`
      : kind === "signal"
      ? `/payments/signal/status/${sessionId}`
      : kind === "rental"
      ? `/payments/rental/status/${sessionId}`
      : `/payments/checkout/status/${sessionId}`;
    const poll = async () => {
      try {
        const { data } = await api.get(endpoint);
        if (kind === "extras") {
          if (data.extras_payment_status === "paid") {
            setStatus("success");
            return;
          }
        } else if (kind === "rental") {
          if (data.rental_payment_status === "paid") {
            setStatus("success");
            return;
          }
        } else if (kind === "signal") {
          if (data.signal_payment_status === "paid") {
            // El resto incluye los extras sin cobrar, que el estado del pago no trae: se leen de la reserva.
            const r = code ? await api.get(`/r/${code}`).then((res) => res.data).catch(() => null) : null;
            setSignalInfo({ balance: r ? restoTrasSenal(r) : 0, deposit: Number(data.deposit_amount || 0) });
            setStatus("success");
            return;
          }
        } else if (data.reservation_payment_status === "paid" || data.reservation_payment_status === "on_hold") {
          setHoldMode(data.reservation_payment_status === "on_hold");
          if (Number(data.extras_amount || 0) > 0) {
            setExtrasInfo({
              amount: Number(data.extras_amount),
              paid: data.extras_payment_status === "paid",
            });
          }
          setStatus("success");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        attempts += 1;
        if (attempts >= MAX_ATTEMPTS) {
          setStatus("timeout");
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (err) {
        setError(formatError(err));
        setStatus("error");
      }
    };
    poll();
  }, [result, sessionId, kind, code]);

  return (
    <div className="min-h-screen bg-[#F7F5F0] flex items-center justify-center p-6">
      <div className="bg-white border border-[#DCD8D0] rounded-2xl p-8 max-w-md w-full text-center fade-in">
        {status === "checking" && (
          <>
            <Loader2 className="h-10 w-10 mx-auto mb-3 text-marca animate-spin" />
            <h2 className="font-display text-xl font-bold text-[#1A1A1A]">
              Procesando pago...
            </h2>
            <p className="text-sm text-[#5C5C5C] mt-1">
              Verificando con Stripe, no cierres esta ventana.
            </p>
          </>
        )}
        {status === "success" && (
          <>
            <div className="h-14 w-14 rounded-full bg-[#4A7C59] text-white flex items-center justify-center mx-auto mb-4">
              <Check className="h-7 w-7" />
            </div>
            <h2 className="font-display text-2xl font-bold text-[#1A1A1A] mb-2">
              {kind === "extras" ? "¡Extras pagados!" : kind === "rental" ? "¡Alquiler pagado!" : kind === "signal" ? "¡Señal recibida!" : holdMode ? "¡Fianza preautorizada!" : "¡Pago recibido!"}
            </h2>
            <p className="text-[#5C5C5C] mb-4">
              {kind === "extras"
                ? "Hemos cobrado los extras de tu reserva. ¡Todo listo para la entrega!"
                : kind === "signal"
                ? `Tu reserva queda confirmada. Unos días antes de la entrega te enviaremos un correo para pagar el resto del alquiler${signalInfo?.balance ? ` (${eur(signalInfo.balance)})` : ""} y la fianza${signalInfo?.deposit ? ` (${signalInfo.deposit.toFixed(0)} €)` : ""}.`
                : kind === "rental"
                ? "Hemos recibido el pago del alquiler de tu reserva. ¡Buen viaje!"
                : holdMode
                ? "Tu tarjeta queda retenida sin cobro real. Liberaremos la fianza tras el check-out sin incidencias."
                : "Hemos cobrado la fianza y te la devolveremos tras la devolución sin incidencias. ¡Disfruta del viaje!"}
            </p>
            {kind !== "extras" && kind !== "rental" && kind !== "signal" && extrasInfo && (
              <div
                className={`rounded-xl p-3 mb-6 text-sm ${
                  extrasInfo.paid
                    ? "bg-[#4A7C59]/10 border border-[#4A7C59]/30 text-[#1A1A1A]"
                    : "bg-[#E89B33]/10 border border-[#E89B33]/30 text-[#9C6512]"
                }`}
                data-testid="payresult-extras"
              >
                {extrasInfo.paid
                  ? `Extras cobrados: ${eur(extrasInfo.amount)} ✓`
                  : `Extras pendientes (${eur(extrasInfo.amount)}): revisa tu email o tu reserva para completar el pago.`}
              </div>
            )}
            <Button
              onClick={() => navigate(`/reserva/${code}`)}
              className="bg-marca hover:bg-marca-hover text-white"
              data-testid="payresult-back-btn"
            >
              Volver a mi reserva
            </Button>
          </>
        )}
        {(status === "cancelled" || status === "expired" || status === "timeout" || status === "error") && (
          <>
            <div className="h-14 w-14 rounded-full bg-[#B54434] text-white flex items-center justify-center mx-auto mb-4">
              <X className="h-7 w-7" />
            </div>
            <h2 className="font-display text-2xl font-bold text-[#1A1A1A] mb-2">
              {status === "cancelled" ? "Pago cancelado" :
               status === "expired" ? "Sesión expirada" :
               status === "timeout" ? "Verificación pendiente" :
               "Error en el pago"}
            </h2>
            <p className="text-[#5C5C5C] mb-6">
              {error || "Puedes intentar el pago nuevamente desde tu reserva."}
            </p>
            <Button
              onClick={() => navigate(`/checkin/${code}`)}
              className="bg-marca hover:bg-marca-hover text-white"
              data-testid="payresult-retry-btn"
            >
              Reintentar
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
